import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Loader2, Shield, BadgeCheck, MoreHorizontal, CalendarPlus, Gift, EyeOff, Eye, Sparkles, AlertTriangle, ClipboardCheck, Leaf, MapPin, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const SUB_UI = {
  trialing: { label: "Trial", color: "bg-blue-100 text-blue-800 border-blue-200" },
  active: { label: "Active", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  past_due: { label: "Past due", color: "bg-amber-100 text-amber-800 border-amber-200" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800 border-red-200" },
  expired: { label: "Expired", color: "bg-red-100 text-red-800 border-red-200" },
};
const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [farms, setFarms] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [listings, setListings] = useState([]);
  const [reports, setReports] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [editFarm, setEditFarm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [f, fm, dr, ls, rp, nt] = await Promise.all([
        api.get("/farms"),
        api.get("/admin/farmers"),
        api.get("/admin/onboarding", { params: { status: "submitted" } }),
        api.get("/admin/listings"),
        api.get("/admin/reports", { params: { status: "open" } }),
        api.get("/admin/notifications"),
      ]);
      setFarms(f.data);
      setFarmers(fm.data);
      setDrafts(dr.data);
      setListings(ls.data);
      setReports(rp.data);
      setNotifs(nt.data);
    } catch (e) { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markNotifsRead = async () => {
    try { await api.put("/admin/notifications/read"); setNotifs((n) => ({ ...n, unread: 0 })); } catch { /* */ }
  };
  const saveListing = async () => {
    setBusy(true);
    try {
      const { id, ...fields } = editFarm;
      await api.put(`/admin/listings/${id}`, fields);
      toast.success("Listing updated");
      setEditFarm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const removeListing = async (farmId, name) => {
    if (!window.confirm(`Remove "${name}"? This deletes the listing and its products from the map.`)) return;
    try { await api.delete(`/admin/listings/${farmId}`); toast.success("Listing removed"); load(); }
    catch { toast.error("Could not remove listing"); }
  };
  const resolveReport = async (rid) => {
    try { await api.put(`/admin/reports/${rid}/resolve`); toast.success("Report resolved"); load(); }
    catch { toast.error("Could not resolve"); }
  };

  if (user && user.role !== "admin") {
    return <div className="pt-32 text-center"><p className="text-muted-foreground">Admin access only.</p><Button className="mt-3" onClick={() => navigate("/")}>Home</Button></div>;
  }

  const toggleVerify = async (farmId) => {
    try { const res = await api.put(`/admin/farms/${farmId}/verify`); toast.success(res.data.verified ? "Verified" : "Unverified"); load(); }
    catch { toast.error("Could not update"); }
  };
  const extendTrial = async (farmId, days) => {
    try { await api.put(`/admin/farmers/${farmId}/extend-trial`, { days }); toast.success(`Trial extended by ${days} days`); load(); }
    catch { toast.error("Could not extend"); }
  };
  const comp = async (farmId) => {
    try { await api.put(`/admin/farmers/${farmId}/comp`); toast.success("Free account granted (active)"); load(); }
    catch { toast.error("Could not comp"); }
  };

  const openDraft = async (id) => {
    try {
      const res = await api.get(`/admin/onboarding/${id}`);
      setDetail(res.data);
      setDetailOpen(true);
    } catch { toast.error("Could not open draft"); }
  };
  const setDF = (k, v) => setDetail((d) => ({ ...d, draft_farm: { ...d.draft_farm, [k]: v } }));
  const setDP = (i, k, v) => setDetail((d) => {
    const products = [...d.draft_products];
    products[i] = { ...products[i], [k]: v };
    return { ...d, draft_products: products };
  });
  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/onboarding/${detail.id}`, { draft_farm: detail.draft_farm, draft_products: detail.draft_products });
      toast.success("Changes saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const approveDraft = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/onboarding/${detail.id}`, { draft_farm: detail.draft_farm, draft_products: detail.draft_products });
      await api.post(`/admin/onboarding/${detail.id}/approve`);
      toast.success("Approved — listing is now live on the map");
      setDetailOpen(false); setDetail(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
    finally { setBusy(false); }
  };
  const rejectDraft = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/onboarding/${detail.id}/reject`, { notes: "" });
      toast.success("Draft rejected");
      setDetailOpen(false); setDetail(null); load();
    } catch (e) { toast.error("Could not reject"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="pt-32 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="pt-16 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 mb-5"><Shield className="h-5 w-5 text-primary" /><h1 className="text-2xl font-semibold">Admin</h1></div>

        <Tabs defaultValue="subscriptions">
          <TabsList>
            <TabsTrigger value="subscriptions" data-testid="admin-tab-subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="verification" data-testid="admin-tab-verification">Verification</TabsTrigger>
            <TabsTrigger value="onboarding" data-testid="admin-tab-onboarding">
              Onboarding
              {drafts.length > 0 && <Badge className="ml-2 bg-[color:var(--fg-wood-600)] text-white h-5 min-w-5 px-1.5">{drafts.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="listings" data-testid="admin-tab-listings">Live listings</TabsTrigger>
            <TabsTrigger value="reports" data-testid="admin-tab-reports">
              Reports
              {reports.length > 0 && <Badge className="ml-2 bg-red-600 text-white h-5 min-w-5 px-1.5">{reports.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="admin-tab-notifications" onClick={markNotifsRead}>
              Activity
              {notifs.unread > 0 && <Badge className="ml-2 bg-[color:var(--fg-wood-600)] text-white h-5 min-w-5 px-1.5">{notifs.unread}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="subscriptions" className="mt-4">
            <Card className="rounded-2xl overflow-hidden" data-testid="admin-farmers-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Farm</TableHead><TableHead>Owner</TableHead><TableHead>Status</TableHead>
                  <TableHead>Trial ends</TableHead><TableHead>Visible</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {farmers.map((f) => (
                    <TableRow key={f.farm_id}>
                      <TableCell className="font-medium">{f.farm_name}<div className="text-xs text-muted-foreground">{f.town}</div></TableCell>
                      <TableCell className="text-sm">{f.owner_name}<div className="text-xs text-muted-foreground">{f.owner_email}</div></TableCell>
                      <TableCell><Badge className={(SUB_UI[f.subscription_status] || SUB_UI.trialing).color} data-testid="admin-sub-status">{(SUB_UI[f.subscription_status] || SUB_UI.trialing).label}</Badge></TableCell>
                      <TableCell className="text-sm">{f.subscription_status === "trialing" ? <>{fmt(f.trial_ends_at)}<div className="text-xs text-muted-foreground">{f.trial_days_left} days left</div></> : "—"}</TableCell>
                      <TableCell>{f.is_visible ? <Eye className="h-4 w-4 text-emerald-600" /> : <EyeOff className="h-4 w-4 text-red-500" />}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" data-testid="admin-farmer-actions"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="z-[1100]">
                            <DropdownMenuItem onClick={() => extendTrial(f.farm_id, 30)} data-testid="admin-extend-30"><CalendarPlus className="h-4 w-4 mr-2" />Extend trial +30 days</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => extendTrial(f.farm_id, 90)}><CalendarPlus className="h-4 w-4 mr-2" />Extend trial +90 days</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => comp(f.farm_id)} data-testid="admin-comp"><Gift className="h-4 w-4 mr-2" />Comp free account (active)</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <p className="text-xs text-muted-foreground mt-3">Only farms with status Trial or Active appear on the public map. Extend a trial or comp a free account for early farmers.</p>
          </TabsContent>

          <TabsContent value="verification" className="mt-4">
            <Card className="rounded-2xl overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Farm</TableHead><TableHead>Location</TableHead><TableHead>Certification</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                <TableBody>
                  {farms.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell className="font-medium">{f.farm_name}</TableCell>
                      <TableCell>{f.town}, {f.postcode}</TableCell>
                      <TableCell>{f.certifications?.[0]?.body || "—"}</TableCell>
                      <TableCell>{f.verified ? <Badge className="bg-[#E6F2E8] text-[#1F5A2F]"><BadgeCheck className="h-3 w-3 mr-1" />Verified</Badge> : <Badge variant="secondary">Unverified</Badge>}</TableCell>
                      <TableCell className="text-right"><Button size="sm" variant="secondary" onClick={() => toggleVerify(f.id)}>{f.verified ? "Unverify" : "Verify"}</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <p className="text-xs text-muted-foreground mt-3">Note: verification table shows currently-visible farms only.</p>
          </TabsContent>

          <TabsContent value="onboarding" className="mt-4">
            <Card className="rounded-2xl overflow-hidden" data-testid="admin-onboarding-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Business</TableHead><TableHead>Type</TableHead><TableHead>Location</TableHead>
                  <TableHead>Products</TableHead><TableHead>Flags</TableHead><TableHead>Submitted</TableHead><TableHead className="text-right">Action</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {drafts.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No listings awaiting review.</TableCell></TableRow>
                  )}
                  {drafts.map((d) => (
                    <TableRow key={d.id} data-testid="admin-onboarding-row">
                      <TableCell className="font-medium">{d.farm_name || "—"}<div className="text-xs text-muted-foreground">{d.user_email}</div></TableCell>
                      <TableCell><Badge variant="secondary">{d.seller_type}</Badge></TableCell>
                      <TableCell className="text-sm">{d.town}{d.postcode ? `, ${d.postcode}` : ""}</TableCell>
                      <TableCell>{d.product_count}</TableCell>
                      <TableCell>{d.flag_count > 0 ? <Badge className="bg-amber-100 text-amber-800 border-amber-200"><AlertTriangle className="h-3 w-3 mr-1" />{d.flag_count}</Badge> : <Badge className="bg-emerald-100 text-emerald-800">Clear</Badge>}</TableCell>
                      <TableCell className="text-sm">{fmt(d.submitted_at)}</TableCell>
                      <TableCell className="text-right"><Button size="sm" onClick={() => openDraft(d.id)} data-testid="admin-review-draft-button"><ClipboardCheck className="h-4 w-4 mr-1" />Review</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <p className="text-xs text-muted-foreground mt-3">Listings created via the AI assistant. Most publish automatically; these are the ones held for review (organic-certified, raw milk, or flagged as incomplete/uncertain). Fix anything flagged, then approve to publish.</p>
          </TabsContent>

          <TabsContent value="listings" className="mt-4">
            <Card className="rounded-2xl overflow-hidden" data-testid="admin-listings-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Business</TableHead><TableHead>Type</TableHead><TableHead>Location</TableHead>
                  <TableHead>Products</TableHead><TableHead>Reports</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {listings.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No live listings yet.</TableCell></TableRow>}
                  {listings.map((l) => (
                    <TableRow key={l.id} data-testid="admin-listing-row">
                      <TableCell className="font-medium">{l.farm_name}</TableCell>
                      <TableCell><Badge variant="secondary">{l.seller_type}</Badge></TableCell>
                      <TableCell className="text-sm">{l.town}{l.postcode ? `, ${l.postcode}` : ""}</TableCell>
                      <TableCell>{l.product_count}</TableCell>
                      <TableCell>{l.open_reports > 0 ? <Badge className="bg-red-100 text-red-800 border-red-200">{l.open_reports}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell><Badge className={(SUB_UI[l.subscription_status] || {}).color || ""}>{(SUB_UI[l.subscription_status] || {}).label || l.subscription_status || "—"}</Badge></TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" data-testid="admin-listing-actions"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => window.open(`/farms/${l.id}`, "_blank")}><Eye className="h-4 w-4 mr-2" />View profile</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditFarm({ id: l.id, farm_name: l.farm_name, seller_type: l.seller_type, town: l.town, postcode: l.postcode, description: "" })} data-testid="admin-listing-edit"><ClipboardCheck className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => removeListing(l.id, l.farm_name)} className="text-red-600" data-testid="admin-listing-remove"><EyeOff className="h-4 w-4 mr-2" />Remove</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="mt-4">
            <Card className="rounded-2xl overflow-hidden" data-testid="admin-reports-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Listing</TableHead><TableHead>Reason</TableHead><TableHead>Reporter</TableHead><TableHead>When</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {reports.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No open reports.</TableCell></TableRow>}
                  {reports.map((r) => (
                    <TableRow key={r.id} data-testid="admin-report-row">
                      <TableCell className="font-medium">{r.farm_name}</TableCell>
                      <TableCell className="text-sm max-w-xs">{r.reason}</TableCell>
                      <TableCell className="text-sm">{r.reporter_email || "—"}</TableCell>
                      <TableCell className="text-sm">{fmt(r.created_at)}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button size="sm" variant="ghost" onClick={() => window.open(`/farms/${r.farm_id}`, "_blank")}>View</Button>
                        <Button size="sm" onClick={() => resolveReport(r.id)} data-testid="admin-resolve-report">Resolve</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="mt-4">
            <Card className="rounded-2xl p-2" data-testid="admin-notifications-list">
              {notifs.items.length === 0 && <p className="text-center text-muted-foreground py-8">No activity yet.</p>}
              <div className="divide-y">
                {notifs.items.map((n) => (
                  <div key={n.id} className="flex items-start gap-3 p-3" data-testid="admin-notification-row">
                    <span className={`mt-0.5 h-8 w-8 rounded-lg grid place-items-center shrink-0 ${n.type === "listing_reported" ? "bg-red-100 text-red-700" : "bg-[#E6F2E8] text-[#1F5A2F]"}`}>
                      {n.type === "listing_reported" ? <AlertTriangle className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{n.title}</div>
                      <div className="text-sm text-muted-foreground">{n.message}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{fmt(n.created_at)}</div>
                    </div>
                    {n.farm_id && <Button size="sm" variant="ghost" onClick={() => window.open(`/farms/${n.farm_id}`, "_blank")}>View</Button>}
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit live listing dialog */}
      <Dialog open={!!editFarm} onOpenChange={(o) => !o && setEditFarm(null)}>
        <DialogContent className="max-w-lg z-[1100]" data-testid="admin-edit-listing-dialog">
          <DialogHeader><DialogTitle>Edit listing</DialogTitle></DialogHeader>
          {editFarm && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Business name</Label><Input value={editFarm.farm_name || ""} onChange={(e) => setEditFarm((f) => ({ ...f, farm_name: e.target.value }))} data-testid="admin-edit-farm-name" /></div>
              <div className="space-y-1.5"><Label>Producer type</Label><Input value={editFarm.seller_type || ""} onChange={(e) => setEditFarm((f) => ({ ...f, seller_type: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Town</Label><Input value={editFarm.town || ""} onChange={(e) => setEditFarm((f) => ({ ...f, town: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>Postcode</Label><Input value={editFarm.postcode || ""} onChange={(e) => setEditFarm((f) => ({ ...f, postcode: e.target.value }))} data-testid="admin-edit-postcode" /></div>
              </div>
              <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={editFarm.description || ""} onChange={(e) => setEditFarm((f) => ({ ...f, description: e.target.value }))} /></div>
              <p className="text-xs text-muted-foreground">Changing the postcode will re-place the map pin.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditFarm(null)}>Cancel</Button>
            <Button onClick={saveListing} disabled={busy} data-testid="admin-save-listing">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding review dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto z-[1100]" data-testid="admin-onboarding-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Review listing</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-5">
              {(detail.flags?.length > 0 || detail.assistant_notes) && (
                <Card className="rounded-xl p-4 border-[#E8C97A] bg-[#FBF3E2]">
                  <div className="flex items-center gap-2 font-medium text-[#7A5A1F] mb-1.5"><AlertTriangle className="h-4 w-4" /> Flagged by the assistant</div>
                  {detail.flags?.length > 0 && (
                    <ul className="list-disc pl-5 text-sm text-[#7A5A1F] space-y-0.5" data-testid="admin-draft-flags">
                      {detail.flags.map((fl, i) => <li key={i}>{fl}</li>)}
                    </ul>
                  )}
                  {detail.assistant_notes && <p className="text-sm text-[#7A5A1F] mt-2 italic">{detail.assistant_notes}</p>}
                </Card>
              )}

              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Business</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Business name</Label><Input value={detail.draft_farm.farm_name || ""} onChange={(e) => setDF("farm_name", e.target.value)} data-testid="admin-draft-farm-name" /></div>
                  <div className="space-y-1.5"><Label>Producer type</Label><Input value={detail.draft_farm.seller_type || ""} onChange={(e) => setDF("seller_type", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label>Town</Label><Input value={detail.draft_farm.town || ""} onChange={(e) => setDF("town", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label>Postcode</Label><Input value={detail.draft_farm.postcode || ""} onChange={(e) => setDF("postcode", e.target.value)} data-testid="admin-draft-postcode" /></div>
                </div>
                <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={detail.draft_farm.description || ""} onChange={(e) => setDF("description", e.target.value)} /></div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="secondary">{detail.draft_farm.collection_available ? "Collection" : "No collection"}</Badge>
                  <Badge variant="secondary">{detail.draft_farm.delivery_available ? `Delivery${detail.draft_farm.delivery_fee ? ` £${detail.draft_farm.delivery_fee}` : ""}` : "No delivery"}</Badge>
                  <Badge variant="secondary">Cert: {detail.draft_farm.cert_type || "none"}{detail.draft_farm.cert_number ? ` · ${detail.draft_farm.cert_number}` : ""}</Badge>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2"><Leaf className="h-4 w-4 text-primary" /> Products ({detail.draft_products.length})</h3>
                {detail.draft_products.map((p, i) => (
                  <Card key={i} className="rounded-xl p-3 space-y-2">
                    <div className="grid grid-cols-[1fr_90px_110px] gap-2">
                      <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={p.name || ""} onChange={(e) => setDP(i, "name", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">£ Price</Label><Input type="number" value={p.price || 0} onChange={(e) => setDP(i, "price", Number(e.target.value))} /></div>
                      <div className="space-y-1"><Label className="text-xs">Unit</Label><Input value={p.unit || ""} onChange={(e) => setDP(i, "unit", e.target.value)} /></div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="secondary" className="text-[11px]">{p.category}</Badge>
                      {p.sourced_from && <span className="text-[11px] text-muted-foreground">Sourced: {p.sourced_from}</span>}
                      {(p.production_tags || []).map((t) => <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#EEF4E6] text-[#3E5A1F] border border-[#cfe0bc] px-2 py-0.5 text-[11px]"><Leaf className="h-3 w-3" />{t}</span>)}
                    </div>
                  </Card>
                ))}
              </div>

              {detail.transcript?.length > 0 && (
                <Accordion type="single" collapsible>
                  <AccordionItem value="t" className="border rounded-xl px-3">
                    <AccordionTrigger className="text-sm"><span className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> View chat transcript</span></AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {detail.transcript.map((m, i) => (
                          <div key={i} className={`text-sm ${m.role === "user" ? "text-foreground" : "text-muted-foreground"}`}>
                            <span className="font-medium">{m.role === "user" ? "Producer" : "Assistant"}: </span>{m.content}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={rejectDraft} disabled={busy} className="text-red-600 hover:text-red-700" data-testid="admin-reject-button">Reject</Button>
            <Button variant="secondary" onClick={saveDraft} disabled={busy} data-testid="admin-save-draft-button">Save changes</Button>
            <Button onClick={approveDraft} disabled={busy} className="bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" data-testid="admin-approve-button">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <BadgeCheck className="h-4 w-4 mr-1" />} Approve &amp; publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
